// src/components/LeadForm.js
/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Form } from 'react-bootstrap';
import SimplePhoneInput from './SimplePhoneInput';

import DateTimePicker from './DateTimePicker';
import { generateDeviceFingerprint, getDevicePlatform } from '../utils/deviceFingerprint';

function toUtcISOString(date) {
  // convert a JS Date (in local TZ) into an ISO string in UTC
  return date.toISOString();
}

// Helper function to extract postcode from address
function extractPostcode(address) {
  if (!address) return null;
  
  // First try full UK postcode pattern (e.g., "EX12 3AB")
  const fullPostcodeRegex = /[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}/i;
  const fullMatch = address.match(fullPostcodeRegex);
  if (fullMatch) {
    return fullMatch[0].toUpperCase();
  }
  
  // If no full postcode found, try postcode area pattern (e.g., "EX12", "EX14")
  const areaPostcodeRegex = /[A-Z]{1,2}[0-9][A-Z0-9]?/i;
  const areaMatch = address.match(areaPostcodeRegex);
  if (areaMatch) {
    return areaMatch[0].toUpperCase();
  }
  
  return null;
}


export default function LeadForm({ from = '', to = '', onClose }) {
  // ── Banners (public) ───────────────────────────────
  const [banners, setBanners] = useState([]);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [bannerRotationInterval, setBannerRotationInterval] = useState(5);

  // Extract postcodes from from/to addresses
  const fromPostcode = extractPostcode(from);
  const toPostcode = extractPostcode(to);

  useEffect(() => {
    (async () => {
      try {
        // Build query parameters for postcode-based banner fetching
        const params = new URLSearchParams();
        if (fromPostcode) params.append('fromPostcode', fromPostcode);
        if (toPostcode) params.append('toPostcode', toPostcode);
        
        console.log('Fetching banners with params:', params.toString());
        const { data } = await axios.get(`/api/admin/public/banners?${params.toString()}`);
        const bannerArray = Array.isArray(data) ? data : [];
        console.log('Fetched banners:', bannerArray.length, 'banners:', bannerArray);
        setBanners(bannerArray);
        
        // Set rotation interval based on first banner or default to 5 seconds
        if (bannerArray.length > 0) {
          setBannerRotationInterval(bannerArray[0].rotationInterval || 5);
        }
      } catch (e) {
        // silently ignore for booking UX
        console.warn('Fetch banners failed', e?.message || e);
      }
    })();
  }, [fromPostcode, toPostcode]);

  // Banner rotation effect
  useEffect(() => {
    if (banners.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentBannerIndex(prev => (prev + 1) % banners.length);
    }, bannerRotationInterval * 1000);

    return () => clearInterval(interval);
  }, [banners.length, bannerRotationInterval]);

  // Record banner impression when banner is shown
  useEffect(() => {
    if (banners.length > 0 && banners[currentBannerIndex]) {
      const recordImpression = async () => {
        try {
          await axios.post(`/admin/banners/${banners[currentBannerIndex]._id}/impression`);
        } catch (e) {
          // silently ignore impression tracking errors
          console.warn('Failed to record banner impression:', e?.message || e);
        }
      };
      recordImpression();
    }
  }, [currentBannerIndex, banners]);

  const handleBannerClick = async (bannerId) => {
    try {
      await axios.post(`/admin/banners/${bannerId}/click`);
    } catch (e) {
      console.warn('Failed to record banner click:', e?.message || e);
    }
  };


  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    from,
    to,
    dateTime: new Date(),
    returnTrip: true,
    passengers: '',
    dog: false,
    disabled: false,
    notes: '',
    isASAP: false,
    mileage: '',
    duration: ''
  });

  useEffect(() => {
    if (from && to) {
      // Calculate route when from/to are provided
      const calculateRoute = () => {
        try {
          // Check if Google Maps is available
          if (!window.google || !window.google.maps || !window.google.maps.DirectionsService) {
            console.warn('Google Maps API not available for route calculation');
            return;
          }

          const svc = new window.google.maps.DirectionsService();
          svc.route({
            origin: from,
            destination: to,
            travelMode: window.google.maps.TravelMode.DRIVING
          }, (resp, status) => {
            try {
              if (status === 'OK' && resp && resp.routes && resp.routes[0] && resp.routes[0].legs && resp.routes[0].legs[0]) {
                const leg = resp.routes[0].legs[0];
                const miles = (leg.distance.value / 1609.34).toFixed(1);
                const mins = Math.round(leg.duration.value / 60);
                setFormData(fd => ({
                  ...fd,
                  mileage: miles,
                  duration: mins
                }));
              } else {
                console.warn('Google Maps route calculation failed:', status);
              }
            } catch (error) {
              console.warn('Error processing Google Maps response:', error);
            }
          });
        } catch (error) {
          console.warn('Error initializing Google Maps route calculation:', error);
        }
      };
      calculateRoute();
    }
  }, [from, to]);

  // "touched" flag for phone validation


  const [leadId, setLeadId] = useState(null);
  const [status, setStatus] = useState('');
  const [operator, setOperator] = useState(null);
  // const [flashIndex, setFlashIndex] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(
    'Sending request to the next available Taxi-Driver...'
  );
  const [recentSubmissions] = useState([]);
  // Deprecated: kept for compatibility if needed in future
  // const [declinedCompanies, setDeclinedCompanies] = useState([]);
  const [notifiedCompanies, setNotifiedCompanies] = useState([]);
  const [listingOnlyCompanies, setListingOnlyCompanies] = useState([]);
  const [unverifiedCompanies, setUnverifiedCompanies] = useState([]);
  const [leadTimeoutMs, setLeadTimeoutMs] = useState(60000);
  const statusRef = useRef({});
  const [recentlyChanged, setRecentlyChanged] = useState({});

  // Function to fetch unverified companies by postcode
  const fetchUnverifiedCompanies = async (fromAddress, toAddress) => {
    try {
      console.log('Fetching unverified companies for addresses:', fromAddress, toAddress);
      
      const fromPostcode = extractPostcode(fromAddress);
      const toPostcode = extractPostcode(toAddress);
      
      console.log('Extracted postcodes:', fromPostcode, toPostcode);
      
      // Try both postcodes if available
      const postcodes = [];
      if (fromPostcode) postcodes.push(fromPostcode);
      if (toPostcode && toPostcode !== fromPostcode) postcodes.push(toPostcode);
      
      console.log('Postcodes to search:', postcodes);
      
      if (postcodes.length === 0) {
        console.log('No postcodes found, skipping unverified companies fetch');
        return;
      }
      
      // Fetch unverified companies for each postcode
      const allCompanies = [];
      for (const postcode of postcodes) {
        try {
          console.log(`Fetching companies for postcode: ${postcode}`);
          const response = await axios.get(`/taxi-companies/public/search/postcode/${postcode}`);
          console.log(`Response for ${postcode}:`, response.data);
          if (response.data && Array.isArray(response.data)) {
            allCompanies.push(...response.data);
          }
        } catch (error) {
          console.warn(`Failed to fetch companies for postcode ${postcode}:`, error);
        }
      }
      
      console.log('All companies found:', allCompanies);
      
      // Remove duplicates and randomize order
      const uniqueCompanies = allCompanies.filter((company, index, self) => 
        index === self.findIndex(c => c._id === company._id)
      );
      
      console.log('Unique companies after deduplication:', uniqueCompanies);
      
      // Randomize using company name as seed for consistent randomization
      const randomizedCompanies = uniqueCompanies.sort((a, b) => {
        const aSeed = (a.company || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
        const bSeed = (b.company || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
        return aSeed - bSeed;
      });
      
      console.log('Final randomized companies:', randomizedCompanies);
      setUnverifiedCompanies(randomizedCompanies);
    } catch (error) {
      console.error('Error fetching unverified companies:', error);
    }
  };



  // keep from/to in sync with props
  useEffect(() => {
    setFormData(fd => ({ ...fd, from, to }));
  }, [from, to]);



  // form field change
  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(fd => ({
      ...fd,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // date picker change - updated to handle new DateTimePicker with ASAP +5 minutes
  const handleDateChange = (date, isASAP = false) => {
    try {
      if (isASAP) {
        // Add 5 minutes to current time when ASAP is toggled
        const now = new Date();
        now.setMinutes(now.getMinutes() + 5);
        setFormData(fd => ({ ...fd, dateTime: now, isASAP }));
      } else {
        setFormData(fd => ({ ...fd, dateTime: date, isASAP }));
      }
    } catch (error) {
      console.warn('Error in handleDateChange:', error);
      // Fallback to current date if there's an error
      setFormData(fd => ({ ...fd, dateTime: new Date(), isASAP }));
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setStatus('');
    setOperator(null);
    setLoading(true);

    // validate phone
    if (!formData.phone || formData.phone.trim() === '') {
      setStatus('Please enter a valid phone number.');
      setLoading(false);
      return;
    }

    // convert to UTC ISO
    const iso = toUtcISOString(formData.dateTime);
    if (!iso) {
      setStatus('Please pick a valid date & time.');
      setLoading(false);
      return;
    }
    // Only validate future date if not ASAP
    if (!formData.isASAP && new Date(iso) < new Date()) {
      setStatus('Please pick a future date & time.');
      setLoading(false);
      return;
    }

    try {
      const { data } = await axios.post('/leads/submit', {
        ...formData,
        passengers: parseInt(formData.passengers, 10),
        dateTime: iso,
        deviceFingerprint: generateDeviceFingerprint(),
        platform: getDevicePlatform()
      });
      setLeadId(data.leadId);
    } catch (err) {
      if (err.response?.status === 409) {
        setStatus('This request has already been submitted.');
      } else if (
        err.response?.status === 400 &&
        typeof err.response.data?.error === 'string' &&
        err.response.data.error.toLowerCase().includes('journey for today')
      ) {
        setStatus('We already have a journey today with that pickup or destination. Please contact the driver directly to make any changes.');
      } else {
        console.error(err);
        setStatus('Submission failed. Please try again.');
      }
      setLoading(false);
    }
  };

  // Load public system settings (timeout)
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const { data } = await axios.get('/admin/public/system-settings');
        if (!isMounted) return;
        if (Number.isFinite(Number(data.leadTimeoutSeconds))) {
          setLeadTimeoutMs(Number(data.leadTimeoutSeconds) * 1000);
        }
      } catch (e) {
        // fallback to default; silent
      }
    })();
    return () => { isMounted = false; };
  }, []);

  // poll status
  useEffect(() => {
    if (!leadId) return;
    const start = Date.now();
    let idx = 0;
    const msgs = [
      'Sending request to the next available Taxi Driver...',
      'Still trying... Hang tight!',
      'Checking another driver now...',
      'Almost there... Looking for a match.'
    ];
    const msgTimer = setInterval(() => {
      idx = (idx + 1) % msgs.length;
      setLoadingMessage(msgs[idx]);
    }, 6000);

    const poll = setInterval(async () => {
      try {
        const { data } = await axios.get(`/leads/status/${leadId}`);
        console.log('📊 Lead status data:', data);
        if (data.status === 'accepted') {
          console.log('✅ Job accepted! Setting operator:', data.acceptedBy);
          setOperator(data.acceptedBy);
          // Ensure companies are updated when job is accepted
          setNotifiedCompanies(data.notifiedBy || []);
          setListingOnlyCompanies(data.listingOnlyCompanies || []);
          clearInterval(poll);
          clearInterval(msgTimer);
          setLoading(false);
        } else if (data.status === 'declined') {
          console.log('Lead declined, declined companies:', data.declinedBy);
          console.log('Notified companies:', data.notifiedBy);
          setStatus('No taxi available right now.');
          // setDeclinedCompanies(data.declinedBy || []);
          setNotifiedCompanies(data.notifiedBy || []);
          setListingOnlyCompanies(data.listingOnlyCompanies || []);
          
          // Fetch unverified companies for the location
          fetchUnverifiedCompanies(formData.from, formData.to);
          
          // Show the contacted companies list immediately upon decline
          setShowAll(true);
          clearInterval(poll);
          clearInterval(msgTimer);
          setLoading(false);
        } else if (Date.now() - start > leadTimeoutMs) {
          console.log('Request timed out, declined companies:', data.declinedBy);
          console.log('Notified companies:', data.notifiedBy);
          setStatus('Request timed out. We\'ll email you if someone becomes available.');
          // setDeclinedCompanies(data.declinedBy || []);
          setNotifiedCompanies(data.notifiedBy || []);
          setListingOnlyCompanies(data.listingOnlyCompanies || []);
          
          // Fetch unverified companies for the location
          fetchUnverifiedCompanies(formData.from, formData.to);
          
          clearInterval(poll);
          clearInterval(msgTimer);
          setLoading(false);
          setShowAll(true);
          setTimedOut(true);
        } else {
          // Pending: keep updating the lists so the UI reflects new responses in real-time
          setNotifiedCompanies(data.notifiedBy || []);
          const nextMap = {};
          (data.notifiedBy || []).forEach(c => {
            const id = String(c._id);
            const prev = statusRef.current[id] || 'pending';
            const curr = c.statusFlag || 'pending';
            nextMap[id] = curr;
            if (prev !== curr && curr !== 'pending') {
              setRecentlyChanged(rc => ({ ...rc, [id]: true }));
              setTimeout(() => {
                setRecentlyChanged(rc => {
                  const copy = { ...rc };
                  delete copy[id];
                  return copy;
                });
              }, 10000);
            }
          });
          statusRef.current = nextMap;
        }
      } catch (err) {
        console.error(err);
      }
    }, 5000);

    return () => {
      clearInterval(poll);
      clearInterval(msgTimer);
    };
  }, [leadId, leadTimeoutMs]);

  // apology email on decline/timeout (only if not accepted)
  useEffect(() => {
    const isValidEmail = /^[^@]+@[^.]+\..+$/.test(formData.email);
    if (operator) return;
    if ((status.includes('No taxi') || status.includes('timed out')) && isValidEmail) {
      axios.post('/leads/notify/unavailable', { email: formData.email }).catch(console.error);
    }
  }, [status, formData.email, operator]);

  const handleCancel = () => {
    setLeadId(null);
    setLoading(false);
    setStatus('');
    setOperator(null);
    // setDeclinedCompanies([]);
    setNotifiedCompanies([]);
    setListingOnlyCompanies([]);
    setUnverifiedCompanies([]);
    setShowAll(false);
    setTimedOut(false);
    onClose?.();
  };

  return (
    <div>
      {/* Banners for customers */}
      <div className="mb-3">
        {banners.length > 0 ? (
          <div 
            key={banners[currentBannerIndex]._id} 
            className="alert alert-info py-2 px-3 mb-2"
            style={{ cursor: 'pointer' }}
            onClick={() => handleBannerClick(banners[currentBannerIndex]._id)}
            title="Click to track engagement"
          >
            {banners[currentBannerIndex].imageDataUrl ? (
              <>
                <img
                  src={banners[currentBannerIndex].imageDataUrl}
                  alt="banner"
                  className="mb-2"
                  style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 4 }}
                />
                <div>
                  <strong>{banners[currentBannerIndex].title}</strong>
                  <div className="small mb-0">{banners[currentBannerIndex].message}</div>
                </div>
              </>
            ) : (
              <div className="d-flex align-items-center gap-2">
                <div>
                  <strong>{banners[currentBannerIndex].title}</strong>
                  <div className="small mb-0">{banners[currentBannerIndex].message}</div>
                </div>
              </div>
            )}
            {/* Banner rotation indicator */}
            {banners.length > 1 && (
              <div className="d-flex justify-content-center mt-2">
                {banners.map((_, index) => (
                  <div
                    key={index}
                    className={`mx-1 rounded-circle ${index === currentBannerIndex ? 'bg-primary' : 'bg-secondary'}`}
                    style={{ width: '8px', height: '8px' }}
                  />
                ))}
              </div>
            )}
            {/* Banner info */}
            <div className="text-center mt-2">
              <small className="text-muted">
                Banner {currentBannerIndex + 1} of {banners.length}
                {banners[currentBannerIndex].targetPostcodes && banners[currentBannerIndex].targetPostcodes.length > 0 && (
                  <span> • Targeted to: {banners[currentBannerIndex].targetPostcodes.join(', ')}</span>
                )}
              </small>
            </div>
          </div>
        ) : (
          <div className="bg-light border rounded p-3 text-center">
            <div className="text-muted">Your local ad here</div>
          </div>
        )}
      </div>
      {/* 1) Form */}
      {!loading && !operator && !status.includes('No taxi') && !status.includes('timed out') && (
        <form onSubmit={handleSubmit} className="card p-4 shadow-sm mb-4">
          {status && <div className="alert alert-warning">{status}</div>}
          <h4 className="mb-4">Your Taxi Fare</h4>

          {/* ── Date & Time (First) - Updated with new DateTimePicker ── */}
          <div className="mb-3">
           <DateTimePicker
              value={formData.dateTime}
              onChange={handleDateChange}
              label="Date & Time"
              className="mb-3"
              use24Hour={false}
              show24HourToggle={false}
            />
          </div>

          {['name','email','from','to'].map(f => (
            <div className="mb-3" key={f}>
              <label className="form-label">{f.charAt(0).toUpperCase()+f.slice(1)}</label>
              <input
                type={f==='email' ? 'email' : 'text'}
                className="form-control"
                name={f}
                value={formData[f]}
                onChange={handleChange}
                required
                readOnly={f==='from'||f==='to'}
              />
            </div>
          ))}

                     {/* ── Mobile Phone with tooltip ── */}
           <Form.Group className="mb-3" controlId="lead-phone">
             <Form.Label>Mobile Phone</Form.Label>
 
             <SimplePhoneInput
               value={formData.phone}
               onChange={val => setFormData(fd => ({ ...fd, phone: val||'' }))}
               placeholder="Enter mobile phone number"
               className={
                 formData.phone &&
                 formData.phone.trim() === ''
                   ? 'is-invalid'
                   : ''
               }
             />
 
             <Form.Control.Feedback type="invalid">
               Invalid phone number
             </Form.Control.Feedback>
             
             
           </Form.Group>

          <div className="form-check mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="returnTrip"
              name="returnTrip"
              checked={formData.returnTrip}
              onChange={handleChange}
            />
            <label className="form-check-label" htmlFor="returnTrip">
              Return Trip
            </label>
          </div>

          <div className="mb-3">
            <label className="form-label">Number of Passengers</label>
            <input
              type="number"
              className="form-control"
              name="passengers"
              value={formData.passengers}
              onChange={handleChange}
              min="1"
              max="20"
              required
              placeholder="Enter number of passengers"
            />
          </div>

          <div className="form-check mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="dog"
              name="dog"
              checked={formData.dog}
              onChange={handleChange}
            />
            <label className="form-check-label" htmlFor="dog">
              Dog/Pet
            </label>
          </div>

          <div className="form-check mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="disabled"
              name="disabled"
              checked={formData.disabled}
              onChange={handleChange}
            />
            <label className="form-check-label" htmlFor="disabled">
              Disabled Access
            </label>
          </div>

          <div className="mb-3">
            <label className="form-label">Notes</label>
            <textarea
              className="form-control"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
              placeholder="Any special requirements..."
            />
          </div>

          <button type="submit" className="btn btn-primary w-100">
            Book Taxi Online
          </button>
        </form>
      )}

      {/* 2) Loading */}
      {loading && (
        <div className="card p-4 shadow-sm mb-4">
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mb-0">{loadingMessage}</p>
            <div className="alert alert-info mt-3 text-start small">
              <strong>What happens next:</strong>
              <div>Best outcome: Job accepted — taxi on the way.</div>
              <div>Worst outcome: We’ll show local taxis to easily call.</div>
            </div>
            <button
              type="button"
              className="btn btn-outline-secondary mt-3"
              onClick={handleCancel}
            >
              Cancel
            </button>
            {/* Show full list while contacting */}
            {notifiedCompanies.length > 0 && (
              <div className="mt-4">
                <div className="mb-2">Contacting local companies…</div>
                <div className="list-group">
                  {notifiedCompanies.map((c, i) => (
                    <div key={i} className="list-group-item d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center gap-3">
                        <img src={c.logo || '/logo192.png'} alt="Logo" style={{height:'40px'}} />
                        <div>
                          <div className="fw-semibold">{c.companyName || c.name}</div>
                          <div className={`text-muted ${recentlyChanged[String(c._id)] ? 'fw-bold text-primary' : ''}`} style={{fontSize:'0.9rem'}}>
                            Status: {' '}
                            <span className="fw-bold text-uppercase">
                              {(
                                c.statusFlag === 'accepted'      ? 'Accepted' :
                                c.statusFlag === 'available30'   ? 'Available In 30 Min' :
                        c.statusFlag === 'available60'   ? 'Available In 1 Hr' :
                                c.statusFlag === 'available90'   ? 'Available In 1.5 Hrs' :
                                c.statusFlag === 'available120'  ? 'Available In 2 Hr' :
                        c.statusFlag === 'fullybooked'   ? 'Fully Booked' :
                        c.statusFlag === 'notavailable'  ? 'Not Available' :
                                c.statusFlag === 'noresponse'    ? 'No Response' :
                                c.statusFlag === 'declined'      ? 'Declined' :
                                'Awaiting Response'
                              )}
                            </span>
                            
                          </div>
                        </div>
                      </div>
                      {/* Call button only appears after timeout */}
                      {timedOut && c.phoneNumber && (
                        <a className="btn btn-sm btn-success" href={`tel:${c.phoneNumber}`}>Call</a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Local advertising banners slot */}
            <div className="mt-4">
              <div className="bg-light border rounded p-3">Your local ad here</div>
            </div>
          </div>
        </div>
      )}

      {/* 3) Success */}
      {operator && (
        <div className="card p-4 shadow-sm mb-4">
          <div className="text-center">
            <div className="text-success mb-3">
              <i className="bi bi-check-circle-fill fs-1"></i>
            </div>
            <h4>Taxi Confirmed!</h4>
            
            {/* Company Name and Logo */}
            <div className="mb-3">
              <div className="d-flex align-items-center justify-content-center mb-2">
                {operator.logo && (
                  <img 
                    src={operator.logo} 
                    alt={`${operator.companyName} logo`}
                    style={{ width: '40px', height: '40px', objectFit: 'contain', marginRight: '10px' }}
                    className="rounded"
                  />
                )}
                <h5 className="mb-0 text-primary">{operator.companyName}</h5>
              </div>
            </div>
            
            {/* Driver Information */}
            <div className="mb-3">
              <div className="d-flex align-items-center justify-content-center mb-2">
                <p className="mb-0 me-2">
                  Your driver is <strong>{operator.name.split(' ')[0]}</strong>
                </p>
                {operator.badgeNumber && (
                  <span className="badge bg-primary">
                    ID: {operator.badgeNumber}
                  </span>
                )}
              </div>
              
              {/* Car Information */}
              {(operator.carMake || operator.carModel) && (
                <div className="mb-3">
                  <div className="d-flex align-items-center justify-content-center mb-2">
                    <div className="car-icon me-2">
                      <i className="bi bi-car-front-fill text-primary" style={{ fontSize: '24px' }}></i>
                    </div>
                    <div className="car-details">
                      <strong>{operator.carMake} {operator.carModel}</strong>
                      {operator.regNumber && (
                        <div className="text-muted small">Reg: {operator.regNumber}</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Licensing Information */}
              <div className="mb-3">
                <div className="row text-center">
                  {operator.licensingAuthority && (
                    <div className="col-md-4 mb-2">
                      <div className="p-2 bg-light rounded">
                        <div className="text-muted small">Licensing Authority</div>
                        <div className="fw-bold">{operator.licensingAuthority}</div>
                      </div>
                    </div>
                  )}
                  {operator.licenseType && (
                    <div className="col-md-4 mb-2">
                      <div className="p-2 bg-light rounded">
                        <div className="text-muted small">License Type</div>
                        <div className="fw-bold">{operator.licenseType}</div>
                      </div>
                    </div>
                  )}
                  {operator.carSeats && (
                    <div className="col-md-4 mb-2">
                      <div className="p-2 bg-light rounded">
                        <div className="text-muted small">Max Seats</div>
                        <div className="fw-bold">{operator.carSeats}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="contact-details">
                <p className="mb-1">
                  <strong>Phone:</strong> 
                  <a href={`tel:${operator.phoneNumber}`} className="ms-2 btn btn-sm btn-outline-primary">
                    {operator.phoneNumber}
                  </a>
                </p>
              </div>
            </div>
            
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleCancel}
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* 4) No taxi available */}
      {status.includes('No taxi') && (
        <div className="card p-4 shadow-sm mb-4">
          <div className="text-center">
            <div className="text-warning mb-3">
              <i className="bi bi-exclamation-triangle-fill fs-1"></i>
            </div>
            <h4>No Taxi Available</h4>
            <p className="mb-3">
              Sorry, no taxi drivers are available at this time.
            </p>
            <p className="mb-3">
              We'll email you if someone becomes available.
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleCancel}
            >
              Try Again
            </button>
          </div>
        </div>
      )}

      {/* 5) Timeout */}
      {status.includes('timed out') && (
        <div className="card p-4 shadow-sm mb-4">
          <div className="text-center">
            <div className="text-warning mb-3">
              <i className="bi bi-clock-fill fs-1"></i>
            </div>
            <h4>Request Timed Out</h4>
            <p className="mb-3">
              Thank you for trying Taxi-Local, unfortunately no company has accepted your enquiry. However, the job will stay active and you may recieve a phone call from a company to help. The list below shows you who has responded, or not. Please now Call them yourself to try and get a taxi.
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleCancel}
            >
              Try Again
            </button>
          </div>
        </div>
      )}

      {/* Show all companies in area after decline, timeout, or acceptance */}
      {(showAll || operator) && (
        <div className="card p-4 shadow-sm mb-4">
          {/* Always show heading when operator is present or showAll is true */}
          <h6 className="bg-dark text-warning text-center mb-3">
            🚖 {operator ? 'Companies Contacted for Your Booking' : 'Verified Companies in Your Required Area'}
          </h6>
          <div className="list-group">
            {(notifiedCompanies.length > 0 || listingOnlyCompanies.length > 0) ? (
              [...notifiedCompanies]
                .sort((a,b)=>{
                  // First, separate companies that have responded from those that haven't
                  const aHasResponded = a.statusFlag && a.statusFlag !== 'pending' && a.statusFlag !== 'noresponse';
                  const bHasResponded = b.statusFlag && b.statusFlag !== 'pending' && b.statusFlag !== 'noresponse';
                  
                  if (aHasResponded && !bHasResponded) return -1; // a comes first
                  if (!aHasResponded && bHasResponded) return 1;  // b comes first
                  
                  // If both have responded, sort by response priority (30min, 1hr, etc.)
                  if (aHasResponded && bHasResponded) {
                    const rank = s => {
                      if (s?.statusFlag === 'accepted') return 0;
                      if (s?.statusFlag === 'available30') return 1;
                      if (s?.statusFlag === 'available60') return 2;
                      if (s?.statusFlag === 'available90') return 3;
                      if (s?.statusFlag === 'available120') return 4;
                      if (s?.statusFlag === 'fullybooked') return 5;
                      if (s?.statusFlag === 'notavailable') return 6;
                      if (s?.statusFlag === 'declined') return 7;
                      return 8;
                    };
                    const r = rank(a) - rank(b);
                    if (r !== 0) return r;
                  }
                  
                  // If both no response, randomize them using a consistent seed
                  if (!aHasResponded && !bHasResponded) {
                    // Use company name as seed for consistent randomization
                    const aSeed = (a.companyName || a.name || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
                    const bSeed = (b.companyName || b.name || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
                    return aSeed - bSeed;
                  }
                  
                  // If same response status, sort alphabetically
                  const an = (a.companyName || a.name || '').toLowerCase();
                  const bn = (b.companyName || b.name || '').toLowerCase();
                  return an.localeCompare(bn);
                })
                .map((c, i) => (
                             <div
                 key={i}
                 className={`list-group-item ${
                   (timedOut && (c.statusFlag === 'available30' || c.statusFlag === 'available60'))
                     ? 'bg-primary bg-opacity-10 border-primary'
                     : ''
                 }`}
               >
                 {/* Line 1: Logo (Top Left) and Company Name (Top Right) */}
                 <div className="d-flex justify-content-between align-items-start mb-2">
                   <img src={c.logo || '/logo192.png'} alt="Logo" style={{height:'40px'}} />
                   <div className="fw-semibold text-end">{c.companyName || c.name}</div>
                 </div>
                 
                 {/* Line 2: Status (Full Width) */}
                 <div className={`text-muted ${recentlyChanged[String(c._id)] ? 'fw-bold text-primary' : ''}`} style={{fontSize:'0.9rem'}}>
                   Status: {' '}
                   <span className="fw-bold text-uppercase">
                     {(
                       c.statusFlag === 'accepted'      ? 'Accepted' :
                       c.statusFlag === 'available30'   ? 'Available In 30 Min' :
                       c.statusFlag === 'available60'   ? 'Available In 1 Hr' :
                       c.statusFlag === 'available90'   ? 'Available In 1.5 Hrs' :
                       c.statusFlag === 'available120'  ? 'Available In 2 Hr' :
                       c.statusFlag === 'fullybooked'   ? 'Fully Booked' :
                       c.statusFlag === 'notavailable'  ? 'Not Available' :
                       c.statusFlag === 'noresponse'    ? 'No Response' :
                       c.statusFlag === 'declined'      ? 'Declined' :
                       c.statusFlag === 'pending'       ? 'NO RESPONSE' :
                       'NO RESPONSE'
                     )}
                   </span>
                 </div>
                 
                 {/* Line 3: PLEASE CALL TO CONFIRM and Call Button */}
                 {timedOut && (
                   <div className="d-flex justify-content-between align-items-center mt-2">
                     <div className="text-primary fw-bold small">PLEASE CALL TO CONFIRM</div>
                     {c.phoneNumber && (
                       <a className="btn btn-sm btn-success" href={`tel:${c.phoneNumber}`}>Call</a>
                     )}
                   </div>
                 )}
               </div>
            ))
            ) : (
              <div className="text-center text-muted py-3">
                <p>No companies available in your area at this time.</p>
              </div>
            )}
          </div>
          
          {/* Show listing only companies if any exist */}
          {listingOnlyCompanies.length > 0 && (
            <>
              <h6 className="mt-4 mb-3 text-muted">Other Verified Companies in required Location, But not using Taxi-Local.uk</h6>
              <div className="list-group">
                                                                   {listingOnlyCompanies
                    .sort((a,b) => {
                      // Randomize listing_only companies using company name as seed
                      const aSeed = (a.companyName || a.name || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
                      const bSeed = (b.companyName || b.name || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
                      return aSeed - bSeed;
                    })
                   .map((c, i) => (
                   <div
                     key={`listing-${i}`}
                     className="list-group-item bg-light"
                   >
                     {/* Line 1: Logo (Top Left) and Company Name (Top Right) */}
                     <div className="d-flex justify-content-between align-items-start mb-1">
                       <img src={c.logo || '/logo192.png'} alt="Logo" style={{height:'25px'}} />
                       <div className="fw-semibold text-end">{c.companyName || c.name}</div>
                     </div>
                     
                     {/* Line 2: Status (Full Width) */}
                     <div className="text-muted" style={{fontSize:'0.9rem'}}>
                       Status: {' '}
                       <span className="fw-bold text-uppercase text-info">
                         View Only - Cannot Accept Jobs
                       </span>
                     </div>
                     
                     {/* Line 3: Call Button */}
                     <div className="d-flex justify-content-end mt-1">
                       {c.phoneNumber && (
                         <a className="btn btn-sm btn-outline-secondary" href={`tel:${c.phoneNumber}`}>Call</a>
                       )}
                     </div>
                   </div>
                 ))}
              </div>
            </>
          )}
          
          {/* Show unverified companies if any exist */}
          {console.log('Rendering unverified companies section, count:', unverifiedCompanies.length)}
          {unverifiedCompanies.length > 0 && (
            <>
              <h6 className="mt-4 mb-3 text-muted">Un-Verified Companies in Your Required Location</h6>
              <div className="list-group">
                {unverifiedCompanies.map((c, i) => (
                  <div
                    key={`unverified-${i}`}
                    className="list-group-item bg-light"
                  >
                    {/* Line 1: Company Name and Phone */}
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="fw-semibold">{c.company}</div>
                      <div className="text-muted">{c.phone}</div>
                    </div>
                    
                    {/* Line 2: Status */}
                    <div className="text-muted small mt-1">
                      <span className="text-uppercase">Un-Verified - Please call directly</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* 6) Recent submissions */}
      {recentSubmissions.length > 0 && (
        <div className="card p-4 shadow-sm">
          <h5>Recent Submissions</h5>
          <div className="list-group">
            {recentSubmissions.map((submission, index) => (
              <div key={index} className="list-group-item">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{submission.name}</strong>
                    <br />
                    <small className="text-muted">
                      {submission.from} → {submission.to}
                    </small>
                  </div>
                  <span className="badge bg-secondary">
                    {submission.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
